import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ChevronDown, ChevronRight, Copy, ExternalLink, Rss, ShieldCheck } from 'lucide-react'
import { api } from '@/lib/api'

async function fetchFeed(url: string): Promise<{ xml: string; items: number; size: number }> {
  const res = await fetch(url)
  const xml = await res.text()
  const items = (xml.match(/<item>/g) ?? []).length
  return { xml, items, size: new Blob([xml]).size }
}

export function RssPage() {
  const [active, setActive] = useState<string>('agn_strat')
  const [aboutOpen, setAboutOpen] = useState(true)
  const { data: feeds = [] } = useQuery({ queryKey: ['rss.index'], queryFn: api.rssIndex })

  const activeFeed = feeds.find((f) => f.agent_id === active)
  const { data: preview, isLoading: previewLoading } = useQuery({
    queryKey: ['rss.feed', activeFeed?.feed_url],
    queryFn: () => fetchFeed(activeFeed!.feed_url),
    enabled: !!activeFeed,
  })

  function copy(url: string) {
    const full = `${window.location.origin}${url}`
    navigator.clipboard.writeText(full)
    toast.success('Feed URL copied')
  }

  return (
    <div className="p-3 md:p-5 max-w-[1400px] mx-auto flex flex-col gap-3.5">
      <header>
        <h1 className="text-xl font-medium">RSS feeds</h1>
        <p className="text-xs text-ink-secondary mt-0.5">
          One feed per agent. WordPress-style RSS 2.0 with CDATA bodies, categories, and dc:creator — point any
          reader at the URL.
        </p>
      </header>

      <section className="bg-surface-muted border-l-4 border-brand-600 p-4 rounded-md">
        <button
          onClick={() => setAboutOpen((v) => !v)}
          className="flex items-center gap-1.5 text-sm font-medium text-ink-primary"
        >
          {aboutOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          About per-agent feeds
        </button>
        {aboutOpen && (
          <div className="mt-3 text-sm leading-relaxed text-ink-secondary flex flex-col gap-2">
            <p className="font-medium text-ink-primary">Why per-agent RSS?</p>
            <ul className="list-disc pl-5 flex flex-col gap-1">
              <li>
                <strong className="text-ink-primary">Universal interop.</strong> Aggregators, Zapier/n8n/IFTTT,
                email readers, other LLMs — all speak RSS natively. Zero integration work per consumer.
              </li>
              <li>
                <strong className="text-ink-primary">Writers subscribe to their agent's feed</strong> to see what
                it has queued up for them without logging in.
              </li>
              <li>
                <strong className="text-ink-primary">Syndication partners</strong> get topical feeds without API
                keys or custom code.
              </li>
              <li>
                <strong className="text-ink-primary">LLM source discovery.</strong> Compliant RSS is how assistants
                and crawlers find sources to cite. The WordPress-style namespace set (wfw, dc, content, sy, slash)
                matches what existing tooling expects.
              </li>
            </ul>
          </div>
        )}
      </section>

      <div className="grid grid-cols-1 md:grid-cols-[360px_1fr] gap-3.5 min-w-0">
        <section className="border border-border rounded-md bg-surface-card p-3 flex flex-col gap-2 h-fit">
          <div className="text-xs font-medium text-ink-secondary mb-1">Agent feeds</div>
          {feeds.map((f) => (
            <button
              key={f.agent_id}
              onClick={() => setActive(f.agent_id)}
              className={`text-left border rounded-md px-2.5 py-2 text-sm transition-colors ${
                active === f.agent_id
                  ? 'border-brand-400 bg-brand-50'
                  : 'border-border hover:bg-surface-muted'
              }`}
            >
              <div className="flex items-center gap-1.5">
                <Rss size={12} className="text-brand-600" />
                <span className="font-medium">{f.agent_name}</span>
              </div>
              <div className="text-[11px] text-ink-tertiary mt-0.5 truncate font-mono">{f.feed_url}</div>
              {f.topics.length > 0 && (
                <div className="flex gap-1 mt-1.5 flex-wrap">
                  {f.topics.slice(0, 3).map((t) => (
                    <span
                      key={t}
                      className="text-[10px] px-1.5 py-0.5 rounded bg-surface-muted text-ink-secondary"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </button>
          ))}
        </section>

        <section className="border border-border rounded-md bg-surface-card flex flex-col min-w-0">
          {activeFeed && (
            <>
              <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium">{activeFeed.agent_name}</div>
                  <code className="text-[11px] text-ink-tertiary font-mono">{activeFeed.feed_url}</code>
                </div>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => copy(activeFeed.feed_url)}
                    className="text-xs border border-border rounded-md px-2 py-1 hover:bg-surface-muted flex items-center gap-1"
                  >
                    <Copy size={12} /> Copy URL
                  </button>
                  <a
                    href={activeFeed.feed_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs border border-border rounded-md px-2 py-1 hover:bg-surface-muted flex items-center gap-1"
                  >
                    <ExternalLink size={12} /> Open raw
                  </a>
                  <a
                    href={`https://validator.w3.org/feed/check.cgi?url=${encodeURIComponent(
                      `${window.location.origin}${activeFeed.feed_url}`,
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs border border-border rounded-md px-2 py-1 hover:bg-surface-muted flex items-center gap-1"
                    title="W3C feed validator"
                  >
                    <ShieldCheck size={12} /> Validate
                  </a>
                </div>
              </div>

              <div className="px-4 py-3 border-b border-border flex gap-4 text-xs text-ink-secondary">
                <span>Items: <strong className="text-ink-primary">{preview?.items ?? '—'}</strong></span>
                <span>
                  Size: <strong className="text-ink-primary">
                    {preview ? `${(preview.size / 1024).toFixed(1)} KB` : '—'}
                  </strong>
                </span>
                <span>
                  Content-Type: <strong className="text-ink-primary">application/rss+xml</strong>
                </span>
              </div>

              <div className="p-4 overflow-auto">
                {previewLoading ? (
                  <div className="text-sm text-ink-tertiary">Fetching feed…</div>
                ) : preview ? (
                  <pre className="text-[11px] leading-snug font-mono whitespace-pre-wrap break-all text-ink-secondary bg-surface-muted/30 border border-border rounded-md p-3 max-h-[540px] overflow-auto">
                    <code className="break-all">{preview.xml}</code>
                  </pre>
                ) : null}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  )
}
